
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

import type { AsyncStatus } from '../../../../../@types/AsyncStatus';
import type { RootState } from '../../../../../store/store';
import { JWTHeader } from '../../../../../utils/JWTHeader';
import tailwindConfig from '../../../../../utils/tailwindColors';
import Badge from '../../../../common/Badge';
import { showToast } from '../../../../common/Notification';
import Table from '../../../../common/Table';

interface RentData {
  [key: string]: unknown;
  'MISES EN LOCATION SUR VOS COPROS': string | React.ReactElement;
  'Moyenne des 3 dernières années': string | React.ReactElement;
}

function CoproRents() {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [data, setData] = useState<RentData[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [error, setError] = useState<string | null>(null);

  const calculateAverage = (values: number[]) => {
    const sum = values.reduce((acc, value) => acc + value, 0);
    return sum / values.length;
  };

  const formatData = (unformattedData: Record<string, any>): RentData[] => {
    const years = Object.keys(unformattedData).map((year) => parseInt(year, 10)).sort((a, b) => b - a);
    const numberOfYearsToAverage = 3; // Define the number of years to average
    const latestYears = years.slice(0, numberOfYearsToAverage);
    const valuesToAverage = latestYears.map((year) => unformattedData[year]);
    const averageValue = calculateAverage(valuesToAverage);

    return [
      {
        'MISES EN LOCATION SUR VOS COPROS': 'Nb de mises en location dans vos copros',
        'Moyenne des 3 dernières années': `${averageValue.toFixed(0)} / an`,
      },
    ];
  };

  useEffect(() => {
    if (!company || !jwt || !company.id) {return;}
    setStatus('PENDING');
    fetch(`/api/companies/${company.id}/stats/copros/loans`, JWTHeader(jwt))
      .then((response) => {
        if (!response.ok) {
          throw new Error('Internal server error');
        }
        return response.json();
      })
      .then((result) => {
        const formattedData = formatData(result);
        setData(formattedData);
        setStatus('SUCCESS');
      })
      .catch((response_error) => {
        console.error('Error fetching rent data :', response_error);
        showToast('error', 'Une erreur est survenue lors du chargement des données.');
        setError(response_error.message);
        setStatus('ERROR');
      });
  }, [company, jwt]);

  const headers = ['MISES EN LOCATION SUR VOS COPROS', 'Moyenne des 3 dernières années'];

  return (
    <div>
      {status === 'PENDING' && (
        <div className="w-full text-center">
          <Table data={[]} headers={headers} tableClass="w-full" />
          <Skeleton count={1} height={30} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-2" />
        </div>
      )}
      {status === 'ERROR'
        && (
          <div className="my-32 text-center">
            <Badge
              mode="error"
              text={error}
            />
          </div>
        )}
      {status === 'SUCCESS' && <Table data={data} headers={headers} tableClass="w-full min-h-16" idKey="MISES EN LOCATION SUR VOS COPROS" />}
    </div>
  );
}

export default CoproRents;
